import React from "react";

import {
  BrowserNotSupportedRounded,
  DownloadingRounded,
  EmojiEventsRounded,
  InfoRounded,
  MenuRounded,
  MilitaryTechRounded,
  RefreshRounded,
} from "@mui/icons-material";
import { Box, Chip, LinearProgress, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import useFirebase from "../../../config/context/firebaseContext";
import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { MainIndexDB } from "../../../config/mainIndexDB";
import { Dev } from "../../../config/util/consoleUtil";
import UtilFunctions from "../../../config/util/utilFunctions";

import ChipMain from "../../../components/InputMain/ChipMain";
import SearchMain from "../../../components/InputMain/SearchMain";
import SelectMain from "../../../components/InputMain/SelectMain";
import MainConfigBanner from "../../../components/MainConfigBanner";
import MainModalBase from "../../../components/MainModalBase";
import MainSubmit from "../../../components/MainSubmit";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";
import storageKey from "../../../config/storageKey";
import { GreyPallet } from "../../../config/styles";
import DataItem from "./components/DataItem";

type AvailableSeachType = "prof" | "comp" | "ref" | "ca";

type ProfDataType = any;
type CompDataType = any;
type DataType = { prof: ProfDataType[]; comp: CompDataType[] };

export default function RatingTab() {
  const navigate = useNavigate();

  const { analyticLog, getConfig } = useFirebase();

  const { setShowSideMenuDrawer } = useMainState();
  const { pushAlert } = useModal();

  const [loadedData, setLoadedData] = React.useState<DataType>({ prof: [], comp: [] });
  const [selectedSearch, setSelectedSearch] = React.useState<AvailableSeachType>("prof");

  const [searchField, setSearchField] = React.useState("");
  const [selectedCA, setSelectedCA] = React.useState<string>();

  const [loadingData, setLoadingData] = React.useState(false);
  const [loadingFilter, setLoadingFilter] = React.useState(false);

  const [profDownloadSize, setProfDownloadSize] = React.useState(0);
  const [compDownloadSize, setCompDownloadSize] = React.useState(0);

  const [dataFiltered, setDataFiltered] = React.useState<any[]>([]);
  const [dataTagged, setDataTagged] = React.useState<any[]>([]);

  const CALCULATE_DOWNLOAD = true;

  const handleDownloadData = async () => {
    try {
      setLoadingData(true);

      const fetchUrlByConfig = async (configName: "rating_prof_url" | "rating_comp_url") => {
        const url = getConfig(configName) as string;

        const blobToIndexedDbJson = async (blobData: Blob) => {
          let fileObject = new File([blobData as Blob], configName);
          await MainIndexDB.saveDataToStore("file-objects", { content: fileObject });
          const fileText = await fileObject.text();
          return JSON.parse(fileText);
        };

        if (CALCULATE_DOWNLOAD) {
          const setter =
            configName === "rating_comp_url" ? setCompDownloadSize : setProfDownloadSize;

          return UtilFunctions.getDownloadSize(url, setter, "blob").then((blobData) =>
            blobToIndexedDbJson(blobData as Blob)
          );
        } else {
          return fetch(url).then(async (response) => {
            let blobData = await response.blob();
            return blobToIndexedDbJson(blobData as Blob);
          });
        }
      };

      const response = await Promise.all([
        fetchUrlByConfig("rating_prof_url"),
        fetchUrlByConfig("rating_comp_url"),
      ]);

      const serverVersion = getConfig("rating_version");
      window.localStorage.setItem(storageKey.RATING_VERSION, serverVersion);

      setLoadedData({ prof: response[0], comp: response[1] });
      pushAlert({ message: "Dados baixados", type: "success" });
    } catch (error) {
      console.log(...new Dev("handleLoadData", error).Error());
      pushAlert({ message: "Erro ao obter dados", type: "error" });
    } finally {
      setLoadingData(false);
    }
  };

  const handleConfirmDownload = () => {
    pushAlert({
      isConfirm: true,
      type: "info",
      message: "Os dados da Avaliação Institucional podem chegar a 20MB. Você deseja continuar?",
      onConfirm: async () => {
        handleDownloadData();
      },
      onDimiss: async () => {
        navigate("/");
      },
    });
    // TODO: Delete database
  };

  const handleLoadLocalData = async () => {
    try {
      const dataRetrieved = await MainIndexDB.retrieveAllStore("file-objects");
      const filesRetrieved: File[] = dataRetrieved.map((item) => item.value.content) as File[];

      let profFile = filesRetrieved.find((file) => file.name === "rating_prof_url");
      let compFile = filesRetrieved.find((file) => file.name === "rating_comp_url");

      if (profFile && compFile) {
        const profData = await JSON.parse(await profFile.text());
        const compData = await JSON.parse(await compFile.text());

        setLoadedData({ prof: profData, comp: compData });
        checkVersion();
      } else {
        handleConfirmDownload();
      }
    } catch (error) {
      console.log(...new Dev("handleLoadLocalData", error).Error());
      pushAlert({ message: "Erro ao obter dados localmente", type: "error" });
    }
  };

  const handleFilterData = () => {
    try {
      setLoadingFilter(true);

      if (selectedSearch === "ca") {
        if (!selectedCA) return;

        const dataToFilter = loadedData.comp;

        const filteredData = dataToFilter.filter((v) =>
          String(v.unidade_responsavel).toLowerCase().includes(selectedCA.toLowerCase())
        );

        const sortedData = filteredData.sort(sortByName);

        setDataFiltered(sortedData);
      } else {
        if (searchField.length < 3) {
          pushAlert({
            message: "O campo de pesquisa precisa de pelo menos 3 caracteres",
            type: "error",
          });
          return;
        }

        const dataToFilter = selectedSearch === "prof" ? loadedData.prof : loadedData.comp;

        const filteredData = dataToFilter.filter((v) =>
          String(
            selectedSearch === "prof"
              ? v.nome_docente
              : selectedSearch === "comp"
              ? v.nome
              : v.codigo
          )
            .toLowerCase()
            .includes(searchField.toLowerCase())
        );

        if (filteredData.length > 80) {
          pushAlert({
            message:
              "Sua pesquisa retornou mais de 80 resultados. Tente procurar algo mais específico.",
            type: "warning",
          });

          return;
        }

        const sortedData = filteredData.sort(sortByName);

        setDataFiltered(sortedData);
      }
    } catch (error) {
      console.log(...new Dev("handleFilterData", error).Error());
      pushAlert({ message: "Erro ao filtrar dados", type: "error" });
    } finally {
      setLoadingFilter(false);
    }
  };

  const handleInfoClick = () => {
    pushAlert({
      type: "default",
      title: "Avaliação Institucional",
      isClosable: true,
      children: (handleClose) => (
        <>
          <Box display={"flex"} alignItems={"center"} mt={1} mb={1}>
            <MainText size="caption" color="regular">
              - Os dados exibidos são relativos aos semestres 2021.1, 2021.2 e 2022.1.
            </MainText>
          </Box>

          <Box display={"flex"} alignItems={"center"} mt={1} mb={1}>
            <MainText size="caption" color="regular">
              - Após observações, foi notado que as notas da avaliação, no geral, cairam após a
              pandemia.
            </MainText>
          </Box>

          <Box mt={1} mb={1}>
            <MainText size="caption" color="regular">
              - Os indicadores coloridos seguem a seguinte regra:
            </MainText>
          </Box>
          <Box ml={2} mt={1} mb={1}>
            <MainText size="caption" color="regular">
              Notas maiores ou igual à 9.2 com DP menor ou igual à 1.9 recebem verde.
            </MainText>
          </Box>
          <Box ml={2} mt={1} mb={1}>
            <MainText size="caption" color="regular">
              Notas maiores ou igual à 8.2 com DP menor ou igual à 2.2 recebem amarelo.
            </MainText>
          </Box>
          <Box ml={2} mt={1} mb={2}>
            <MainText size="caption" color="regular">
              Todas as notas menores que 8.2 ou DP maior que 2.2 recebem vermelho.
            </MainText>
          </Box>

          <Chip
            label={
              <Box display={"flex"} alignItems={"center"} style={{ color: GreyPallet.Regular }}>
                <RefreshRounded fontSize={"small"} sx={{ mr: 1 }} />
                <MainText size="small" color="regular">
                  Atualizar Dados da Avaliação
                </MainText>
              </Box>
            }
            variant={"filled"}
            style={{ borderWidth: 3 }}
            onClick={handleConfirmDownload}
          />
        </>
      ),
    });
  };

  const checkVersion = () => {
    const localVersion = window.localStorage.getItem(storageKey.RATING_VERSION);
    const serverVersion = getConfig("rating_version");

    if (serverVersion !== localVersion) {
      pushAlert({
        isConfirm: true,
        type: "warning",
        message: "Você está usando uma versão desatualizada dos dados. Deseja atualizar agora?",
        onConfirm: async () => {
          handleConfirmDownload();
        },
      });
    }
  };

  //

  const sortByGrade = (a: any, b: any) => {
    if (a.media_atuacao_profissional_media < b.media_atuacao_profissional_media) return 1;
    if (a.media_atuacao_profissional_media > b.media_atuacao_profissional_media) return -1;
    return 0;
  };

  const sortByName = (a: any, b: any) => {
    const field = "nome_docente" in a ? "nome_docente" : "nome";
    if (a[field] < b[field]) return -1;
    if (a[field] > b[field]) return 1;
    return 0;
  };

  const handleAddGrade = (item: any) => {
    const newState = [...dataTagged, item];
    setDataTagged(newState.sort(sortByGrade));
  };

  const handleRemoveGrade = (item: any) => {
    const newState = dataTagged.filter((i) => i?.id_docente !== item?.id_docente);
    setDataTagged(newState.sort(sortByGrade));
  };

  //

  React.useEffect(() => {
    analyticLog("click_rating_search", { search: selectedSearch });
    setDataFiltered([]);
    if (searchField) handleFilterData();
  }, [selectedSearch]);

  React.useEffect(() => {
    if (selectedSearch === "ca") handleFilterData();
  }, [selectedCA]);

  React.useEffect(() => {
    analyticLog("click_rating_tab");
    handleLoadLocalData();
  }, []);

  return (
    <Box style={{ width: "100%" }}>
      <PageHeader
        title="Avaliação"
        leftIcon={<MenuRounded />}
        onLeftAction={() => setShowSideMenuDrawer(true)}
        rightIcon={<InfoRounded />}
        onRightAction={handleInfoClick}
      />

      <MainConfigBanner screen="rating" />

      {loadingData && CALCULATE_DOWNLOAD && (
        <MainModalBase onClose={() => {}}>
          <Box
            p={2}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
            gap={1}
          >
            <Box display={"flex"} gap={1} justifyContent="center" alignItems={"center"}>
              <DownloadingRounded />
              <MainText>Baixando Dados</MainText>
            </Box>
            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} gap={1}>
              <Box
                width={50}
                textAlign={"right"}
                sx={{ "& *": { fontFamily: "monospace !important" } }}
              >
                <MainText>
                  {UtilFunctions.formatBytes(profDownloadSize + compDownloadSize).split(" ")[0]}
                </MainText>
              </Box>
              <Box width={50}>
                <MainText>
                  {UtilFunctions.formatBytes(profDownloadSize + compDownloadSize).split(" ")[1]}
                </MainText>
              </Box>
            </Box>
          </Box>
          <LinearProgress color="inherit" />
        </MainModalBase>
      )}

      <MainText size="caption" color="black">
        Avaliação Institucional UFRN
      </MainText>

      <Box mt={2}>
        <MainText size="small" color="regular">
          As informações presentes neste relatório são compiladas dos Dados Abertos da UFRN e por
          esse motivo não expressam nenhuma opinião pessoal do desenvolvedor para com os
          professores.
        </MainText>
      </Box>

      <Box mt={1} mb={3}>
        <MainText size="small" color="regular">
          Para mais informações clique no ícone do canto superior direito.
        </MainText>
      </Box>

      {dataTagged.length > 0 ? (
        <Box>
          <Box mt={2} mb={1}>
            <MainText size="caption" color="black">
              Professores selecionados:
            </MainText>
            <MainText size="small" color="regular">
              Ordenados por nota
            </MainText>
          </Box>

          {dataTagged.map((item, i) => {
            const key = "id_docente" in item ? `${item.id_docente}-${i}` : `${item.codigo}-${i}`;
            const isTagged = dataTagged.some((i) => i?.id_docente === item?.id_docente);
            return (
              <DataItem
                key={key}
                data={item}
                tagIcon={i === 0 ? EmojiEventsRounded : i === 1 ? MilitaryTechRounded : undefined}
                tagAction={() => {
                  if (isTagged) {
                    setDataTagged(dataTagged.filter((i) => i?.id_docente !== item?.id_docente));
                  } else {
                    setDataTagged([...dataTagged, item]);
                  }
                }}
                isTagged={isTagged}
              />
            );
          })}
        </Box>
      ) : null}

      <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} mt={2} mb={1}>
        <MainText size="caption" color="black">
          Pesquisar por:
        </MainText>
      </Box>

      <Box mb={4} gap={1} display={"flex"} flexWrap={"wrap"}>
        <ChipMain
          options={[
            { name: "Nome do Professor", value: "prof" },
            { name: "Nome do Componente", value: "comp" },
            { name: "Código do Componente", value: "ref" },
            { name: "Unidade Acadêmica", value: "ca" },
          ]}
          onChange={(value) => setSelectedSearch(value as AvailableSeachType)}
          value={selectedSearch}
        />
      </Box>

      <MainSubmit onSubmit={handleFilterData}>
        {selectedSearch === "ca" ? (
          <SelectMain
            name="Unidade Acadêmica"
            value={selectedCA || ""}
            options={UtilFunctions.availableCA.map((UAItem) => ({ name: UAItem, value: UAItem }))}
            onChange={setSelectedCA}
          />
        ) : (
          <SearchMain
            name={"Pesquisar"}
            value={searchField}
            onChange={setSearchField}
            onSubmit={handleFilterData}
            loading={loadingFilter}
            disabled={loadingData}
            small={"Maiúsculas não são diferenciadas. Apenas acentos."}
          />
        )}
      </MainSubmit>

      <Box style={{ width: "100%", overflowX: "hidden" }} mt={2}>
        {loadingFilter || loadingData ? (
          <Skeleton
            animation="wave"
            variant="rounded"
            width={"100%"}
            height={80}
            sx={{ borderRadius: "15px", mb: 3 }}
          />
        ) : dataFiltered.length === 0 ? (
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <BrowserNotSupportedRounded />
            <MainText size="caption" color="black">
              Nenhum resultado encontrado...
            </MainText>
          </Box>
        ) : (
          <>
            <Box mb={1}>
              <MainText size="small" color="regular">
                *Clique na bolinha para ver detalhes
              </MainText>
            </Box>
            {dataFiltered.map((item, i) => {
              const key = "id_docente" in item ? `${item.id_docente}-${i}` : `${item.codigo}-${i}`;
              const isTagged = dataTagged.some((i) => i?.id_docente === item?.id_docente);
              return (
                <DataItem
                  key={key}
                  data={item}
                  tagAction={() => {
                    if (isTagged) handleRemoveGrade(item);
                    else handleAddGrade(item);
                  }}
                  isTagged={isTagged}
                />
              );
            })}
          </>
        )}
      </Box>
    </Box>
  );
}
